import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { brl } from '../utils/format';
import { format, addDays, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, CalendarIcon, X, UserX, Plus, Trash2, Maximize2, Minimize2, Wallet, Upload } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BookingLinkModal from '../components/BookingLinkModal';

const STATUS_CFG = {
  scheduled: { label: 'Agendado', cls: 'bg-orange-500/20 text-orange-400' },
  completed: { label: 'Concluído', cls: 'bg-green-500/20 text-green-400' },
  cancelled: { label: 'Cancelado', cls: 'bg-zinc-700 text-zinc-400' },
  no_show: { label: 'Não compareceu', cls: 'bg-red-500/20 text-red-400' },
};

const DIAS = [
  ['segunda', 'Segunda'], ['terca', 'Terça'], ['quarta', 'Quarta'], ['quinta', 'Quinta'],
  ['sexta', 'Sexta'], ['sabado', 'Sábado'], ['domingo', 'Domingo'],
];

const TABS = [
  { key: 'scheduled', label: 'Ativos' },
  { key: 'cancelled', label: 'Cancelados' },
  { key: 'completed', label: 'Concluídos' },
  { key: 'no_show', label: 'Não compareceu' },
];

const FORMA_PAGAMENTO_LABEL = { dinheiro: 'Dinheiro', pix: 'Pix', cartao: 'Cartão' };

// O Mongo devolve created_at sem fuso (naive) mesmo sendo UTC — sem isso o navegador
// interpreta a string como hora LOCAL, deslocando o cálculo (mesma correção do agenda-pwa).
function parseBackendDate(s) {
  const semMicros = s.replace(/(\.\d{3})\d+/, '$1');
  const temFuso = /(Z|[+-]\d{2}:?\d{2})$/.test(semMicros);
  return new Date(temFuso ? semMicros : semMicros + 'Z');
}

function startDateTime(date, start_time) {
  const [h, m] = start_time.split(':').map(Number);
  const [y, mo, d] = date.split('-').map(Number);
  return new Date(y, mo - 1, d, h, m);
}

// Progresso vai de 0% (momento em que foi agendado) a 100% (hora do atendimento).
function progressoInfo(a, now) {
  const created = parseBackendDate(a.created_at);
  const start = startDateTime(a.date, a.start_time);
  const total = start - created;
  const restanteMin = (start - now) / 60000;
  let percent = total > 0 ? ((now - created) / total) * 100 : 100;
  percent = Math.min(100, Math.max(0, percent));
  return { percent, restanteMin };
}

function AppointmentCard({ a, showDate, onPagamento, onNoShow, onCancel, onDelete }) {
  const cfg = STATUS_CFG[a.status] || STATUS_CFG.scheduled;
  const servicosTexto = a.servicos && a.servicos.length > 0 ? a.servicos.map(s => s.descricao).join(', ') : a.descricao;
  return (
    <div className="bg-[var(--tema-bg-subcard)] border border-[var(--tema-border)] rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[var(--tema-text)] font-medium text-sm">
            {showDate && `${format(new Date(`${a.date}T00:00:00`), 'dd/MM')} — `}{a.start_time} – {a.end_time}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>
        </div>
        <div className="text-lg text-[var(--tema-text)] mt-1">{a.client_nome}</div>
        {servicosTexto && <div className="text-xl text-[var(--tema-highlight)]">{servicosTexto}</div>}
        {a.venda_id && (
          <div className="text-sm text-[var(--tema-text-muted)] mt-1">
            Pago: {brl(a.venda_valor_total)} ({FORMA_PAGAMENTO_LABEL[a.venda_forma_pagamento] || a.venda_forma_pagamento})
          </div>
        )}
      </div>
      {a.status === 'scheduled' && (
        <div className="flex items-center gap-2 shrink-0">
          <button title="Ir para pagamento" onClick={() => onPagamento(a)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-[var(--tema-accent-soft)] text-[var(--tema-accent)] hover:opacity-75 transition-opacity">
            <Wallet size={18} />
          </button>
          <button title="Não compareceu" onClick={() => onNoShow(a.id)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-[var(--tema-accent-soft)] text-[var(--tema-accent)] hover:opacity-75 transition-opacity">
            <UserX size={18} />
          </button>
          <button title="Cancelar agendamento" onClick={() => onCancel(a)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-[var(--tema-accent-soft)] text-[var(--tema-accent)] hover:opacity-75 transition-opacity">
            <X size={18} />
          </button>
          <button title="Excluir agendamento" onClick={() => onDelete(a)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-red-500/40 text-red-400 hover:opacity-75 transition-opacity">
            <Trash2 size={18} />
          </button>
        </div>
      )}
      {a.status === 'completed' && !a.venda_id && (
        <div className="flex items-center gap-2 shrink-0">
          <button title="Ir para pagamento" onClick={() => onPagamento(a)}
            className="w-9 h-9 flex items-center justify-center rounded-md border border-[var(--tema-accent-soft)] text-[var(--tema-accent)] hover:opacity-75 transition-opacity">
            <Wallet size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

const emptySettings = () => ({
  duracao_slot_min: 60,
  capacidade: 1,
  horarios: Object.fromEntries(DIAS.map(([k]) => [k, { aberto: false, inicio: '08:00', fim: '18:00' }])),
  datas_bloqueadas: [],
  mensagem_boas_vindas: '',
  antecedencia_minima_horas: 0,
  lembrete_antecedencia_min: 30,
  fundo_imagem: null,
});

export default function Agenda() {
  const location = useLocation();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthAppointments, setMonthAppointments] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(emptySettings());
  const [savingSettings, setSavingSettings] = useState(false);
  const [novaDataBloqueada, setNovaDataBloqueada] = useState('');
  const [companyLink, setCompanyLink] = useState(null);
  const [slug, setSlug] = useState(null);
  const [slugInput, setSlugInput] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [vendaTarget, setVendaTarget] = useState(null);
  const [vendaItens, setVendaItens] = useState([]);
  const [vendaFormaPagamento, setVendaFormaPagamento] = useState('dinheiro');
  const [savingVenda, setSavingVenda] = useState(false);
  const [catalogoServicos, setCatalogoServicos] = useState([]);
  const [tema, setTema] = useState('masculino');
  const [waReady, setWaReady] = useState(null);
  const [tab, setTab] = useState('scheduled');
  const [fullscreen, setFullscreen] = useState(false);
  const [relogio, setRelogio] = useState(new Date());
  const fsRef = useRef(null);

  const dateKey = format(date, 'yyyy-MM-dd');
  const monthStart = format(startOfMonth(date), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(date), 'yyyy-MM-dd');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/agenda/appointments', { params: { date: dateKey } });
      setAppointments(res.data);
    } catch { toast.error('Erro ao carregar agenda'); }
    finally { setLoading(false); }
  }, [dateKey]);

  const loadMonth = useCallback(async () => {
    setLoadingMonth(true);
    try {
      const res = await api.get('/agenda/appointments', { params: { from: monthStart, to: monthEnd } });
      setMonthAppointments(res.data);
    } catch { toast.error('Erro ao carregar agenda do mês'); }
    finally { setLoadingMonth(false); }
  }, [monthStart, monthEnd]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMonth(); }, [loadMonth]);

  useEffect(() => {
    api.get('/settings/').then(res => setTema(res.data.tema || 'masculino')).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/whatsapp/status').then(res => setWaReady(!!res.data.ready)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/services/').then(res => setCatalogoServicos(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.dataset.tema = tema;
    return () => { document.documentElement.dataset.tema = 'masculino'; };
  }, [tema]);

  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setFullscreen(false); };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    if (fullscreen && fsRef.current && !document.fullscreenElement) {
      fsRef.current.requestFullscreen?.().catch(() => {});
    }
  }, [fullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    const refreshInterval = setInterval(load, 30000);
    const clockInterval = setInterval(() => setRelogio(new Date()), 1000);
    return () => { clearInterval(refreshInterval); clearInterval(clockInterval); };
  }, [fullscreen, load]);

  const sairFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    setFullscreen(false);
  };

  const confirmarCancelamento = async () => {
    const id = cancelTarget.id;
    setCancelTarget(null);
    try {
      await api.put(`/agenda/appointments/${id}/cancel`);
      toast.success('Agendamento cancelado');
      load();
      loadMonth();
    } catch { toast.error('Erro ao cancelar'); }
  };

  const confirmarExclusao = async () => {
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await api.delete(`/agenda/appointments/${id}`);
      toast.success('Agendamento excluído');
      load();
      loadMonth();
    } catch { toast.error('Erro ao excluir'); }
  };

  const marcarStatus = async (id, status) => {
    try {
      await api.put(`/agenda/appointments/${id}/complete`, { status });
      toast.success('Status atualizado');
      load();
      loadMonth();
    } catch { toast.error('Erro ao atualizar status'); }
  };

  const abrirPagamento = (a) => {
    const itens = a.servicos && a.servicos.length > 0
      ? a.servicos.map(s => ({ descricao: s.descricao, valor: s.valor }))
      : [{ descricao: a.descricao || '', valor: 0 }];
    setVendaTarget(a);
    setVendaItens(itens);
    setVendaFormaPagamento('dinheiro');
  };

  const setVendaItemDescricao = (idx, descricao) => {
    setVendaItens(p => p.map((it, i) => i === idx ? { ...it, descricao } : it));
  };

  const setVendaItemValor = (idx, raw) => {
    const digits = raw.replace(/\D/g, '');
    const valor = digits ? Number(digits) / 100 : 0;
    setVendaItens(p => p.map((it, i) => i === idx ? { ...it, valor } : it));
  };

  const adicionarVendaItemDoServico = (servicoId) => {
    const servico = catalogoServicos.find(s => s.id === servicoId);
    if (!servico) return;
    setVendaItens(p => [...p, { descricao: servico.descricao, valor: servico.valor }]);
  };

  const removerVendaItem = (idx) => setVendaItens(p => p.filter((_, i) => i !== idx));

  const confirmarPagamento = async () => {
    const itensValidos = vendaItens.filter(it => it.descricao.trim() && Number(it.valor) > 0);
    if (itensValidos.length === 0) {
      toast.error('Informe ao menos um item com descrição e valor');
      return;
    }
    setSavingVenda(true);
    try {
      await api.post(`/agenda/appointments/${vendaTarget.id}/venda`, {
        itens: itensValidos.map(it => ({ descricao: it.descricao, valor: Number(it.valor) })),
        forma_pagamento: vendaFormaPagamento,
      });
      toast.success('Pagamento registrado');
      setVendaTarget(null);
      load();
      loadMonth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar pagamento');
    } finally { setSavingVenda(false); }
  };

  const abrirLinkEmpresa = async () => {
    try {
      const res = await api.post('/agenda/booking-link');
      setCompanyLink(res.data.link);
      setSlug(res.data.slug || null);
      setSlugInput(res.data.slug || '');
    } catch { toast.error('Erro ao gerar link de agendamento'); }
  };

  const copiarLinkEmpresa = () => {
    navigator.clipboard.writeText(companyLink);
    toast.success('Link copiado');
  };

  const salvarSlug = async () => {
    setSavingSlug(true);
    try {
      const res = await api.put('/agenda/slug', { slug: slugInput });
      setSlug(res.data.slug);
      setSlugInput(res.data.slug);
      setCompanyLink(res.data.link);
      toast.success('Endereço do link salvo');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar endereço');
    } finally { setSavingSlug(false); }
  };

  const abrirConfiguracoes = async () => {
    setShowSettings(true);
    try {
      const res = await api.get('/agenda/settings');
      const loaded = res.data.horarios || {};
      const horarios = Object.fromEntries(DIAS.map(([key]) => {
        const d = loaded[key] || {};
        return [key, { aberto: d.aberto ?? false, inicio: d.inicio || '08:00', fim: d.fim || '18:00' }];
      }));
      setSettings({
        duracao_slot_min: res.data.duracao_slot_min ?? 60,
        capacidade: res.data.capacidade ?? 1,
        horarios,
        datas_bloqueadas: res.data.datas_bloqueadas || [],
        mensagem_boas_vindas: res.data.mensagem_boas_vindas || '',
        antecedencia_minima_horas: res.data.antecedencia_minima_horas ?? 0,
        lembrete_antecedencia_min: res.data.lembrete_antecedencia_min ?? 30,
        fundo_imagem: res.data.fundo_imagem || null,
      });
    } catch { toast.error('Erro ao carregar configurações da agenda'); }
  };

  // Submenu "Agenda" na sidebar navega pra cá passando o modal a abrir (não são rotas próprias)
  useEffect(() => {
    const openModal = location.state?.openModal;
    if (openModal === 'link') abrirLinkEmpresa();
    else if (openModal === 'config') abrirConfiguracoes();
    if (openModal) navigate(location.pathname, { replace: true, state: {} });
  }, [location.state]);

  const handleFundoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSettings(p => ({ ...p, fundo_imagem: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const setDia = (dia, campo, valor) => {
    setSettings(p => ({ ...p, horarios: { ...p.horarios, [dia]: { ...p.horarios[dia], [campo]: valor } } }));
  };

  const adicionarDataBloqueada = () => {
    if (!novaDataBloqueada || settings.datas_bloqueadas.includes(novaDataBloqueada)) return;
    setSettings(p => ({ ...p, datas_bloqueadas: [...p.datas_bloqueadas, novaDataBloqueada].sort() }));
    setNovaDataBloqueada('');
  };

  const removerDataBloqueada = (d) => {
    setSettings(p => ({ ...p, datas_bloqueadas: p.datas_bloqueadas.filter(x => x !== d) }));
  };

  const salvarConfiguracoes = async () => {
    setSavingSettings(true);
    try {
      await api.put('/agenda/settings', settings);
      toast.success('Configurações da agenda salvas');
      setShowSettings(false);
    } catch { toast.error('Erro ao salvar configurações'); }
    finally { setSavingSettings(false); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      {waReady === false && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm text-orange-400">Notificações por WhatsApp desativadas até você conectar seu número.</p>
          <Link to="/whatsapp" className="text-sm font-medium text-orange-400 underline hover:text-orange-300">
            Conectar WhatsApp
          </Link>
        </div>
      )}

      {/* Abas: filtram os dois cards abaixo por status */}
      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-[var(--tema-accent)] text-white'
                : 'bg-zinc-900 text-[var(--tema-text-muted)] border border-zinc-800 hover:text-[var(--tema-text)]'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Macro card: agendamentos do dia */}
      <div className="bg-[var(--tema-bg-card)] border border-[var(--tema-border)] rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading font-semibold text-[var(--tema-text)]">Agendamentos do dia</h2>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="flex items-center gap-2">
              <button onClick={() => setDate(d => subDays(d, 1))} className="p-2 text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-lg transition-colors">
                <ChevronLeft size={16} />
              </button>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 px-4 py-2 bg-[var(--tema-bg-subcard)] border border-[var(--tema-border)] rounded-lg text-sm text-[var(--tema-text)] hover:border-[var(--tema-accent-soft)] transition-colors">
                    <CalendarIcon size={15} className="text-[var(--tema-accent)] shrink-0" />
                    {format(date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} locale={ptBR} />
                </PopoverContent>
              </Popover>
              <button onClick={() => setDate(d => addDays(d, 1))} className="p-2 text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-lg transition-colors">
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setDate(new Date())} className="px-3 py-2 text-xs text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-lg transition-colors">
                Hoje
              </button>
              <button onClick={() => setFullscreen(true)} title="Maximizar"
                className="p-2 text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-lg transition-colors">
                <Maximize2 size={16} />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="w-6 h-6 border-2 border-[var(--tema-accent)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : appointments.filter(a => a.status === tab).length === 0 ? (
          <div className="text-center py-8 text-[var(--tema-text-muted)] text-sm">Nenhum agendamento nesta data</div>
        ) : (
          <div className="space-y-2">
            {appointments.filter(a => a.status === tab).map(a => (
              <AppointmentCard key={a.id} a={a}
                onPagamento={abrirPagamento}
                onNoShow={id => marcarStatus(id, 'no_show')}
                onCancel={setCancelTarget}
                onDelete={setDeleteTarget} />
            ))}
          </div>
        )}
      </div>

      {/* Macro card: agendamentos do mês */}
      <div className="bg-[var(--tema-bg-card)] border border-[var(--tema-border)] rounded-xl p-4 space-y-4">
        <h2 className="font-heading font-semibold text-[var(--tema-text)] capitalize">
          Agendamentos de {format(date, "MMMM 'de' yyyy", { locale: ptBR })}
        </h2>

        {loadingMonth ? (
          <div className="flex justify-center py-10">
            <div className="w-6 h-6 border-2 border-[var(--tema-accent)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : monthAppointments.filter(a => a.status === tab).length === 0 ? (
          <div className="text-center py-8 text-[var(--tema-text-muted)] text-sm">Nenhum agendamento neste mês</div>
        ) : (
          <div className="space-y-2">
            {monthAppointments.filter(a => a.status === tab).map(a => (
              <AppointmentCard key={a.id} a={a} showDate
                onPagamento={abrirPagamento}
                onNoShow={id => marcarStatus(id, 'no_show')}
                onCancel={setCancelTarget}
                onDelete={setDeleteTarget} />
            ))}
          </div>
        )}
      </div>

      {/* Tela maximizada: só agendamentos ativos do dia, texto grande, auto-atualiza a cada 30s */}
      {fullscreen && (
        <div ref={fsRef} className="fixed inset-0 z-[200] bg-[var(--tema-bg-subcard)] p-10 overflow-y-auto">
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold text-[var(--tema-accent)]">Agendamentos de hoje</h1>
              <p className="text-xl text-[var(--tema-text-muted)] capitalize mt-1">
                {format(date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </p>
            </div>
            <div className="text-right">
              <div className="text-5xl font-mono font-bold text-[var(--tema-text)]">{format(relogio, 'HH:mm:ss')}</div>
              <button onClick={sairFullscreen}
                className="mt-3 flex items-center gap-2 text-sm text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-lg px-3 py-1.5 ml-auto transition-colors">
                <Minimize2 size={14} /> Sair
              </button>
            </div>
          </div>

          {appointments.filter(a => a.status === 'scheduled').length === 0 ? (
            <div className="text-center py-24 text-2xl text-[var(--tema-text-muted)]">Nenhum agendamento ativo hoje</div>
          ) : (
            <div className="space-y-4">
              {appointments.filter(a => a.status === 'scheduled')
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map(a => {
                  const { percent, restanteMin } = progressoInfo(a, relogio);
                  const urgente = restanteMin <= 30;
                  const corAnel = urgente ? 'var(--tema-highlight)' : 'var(--tema-accent)';
                  const legenda = restanteMin <= 0 ? 'Em andamento' : `${Math.max(1, Math.round(restanteMin))} min`;
                  return (
                    <div key={a.id} className="bg-[var(--tema-bg-card)] border border-[var(--tema-border)] rounded-2xl p-6 flex items-center gap-6">
                      <div className="text-3xl font-mono font-bold text-[var(--tema-accent)] w-36 shrink-0">{a.start_time}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-2xl font-semibold text-[var(--tema-text)]">{a.client_nome}</div>
                        {(a.servicos && a.servicos.length > 0 ? a.servicos.map(s => s.descricao).join(', ') : a.descricao) && (
                          <div className="text-xl text-[var(--tema-highlight)] mt-1">
                            {a.servicos && a.servicos.length > 0 ? a.servicos.map(s => s.descricao).join(', ') : a.descricao}
                          </div>
                        )}
                      </div>
                      <div
                        className="relative w-20 h-20 rounded-full shrink-0"
                        style={{ background: `conic-gradient(${corAnel} ${percent}%, rgba(127,127,127,0.15) ${percent}%)` }}
                      >
                        <div className="absolute inset-2 rounded-full bg-[var(--tema-bg-card)] flex items-center justify-center text-center px-1">
                          <span className="text-xs font-semibold" style={{ color: urgente ? 'var(--tema-highlight)' : 'var(--tema-text-muted)' }}>
                            {legenda}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Modal de confirmação de cancelamento de agendamento */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[var(--tema-bg-card)] border border-[var(--tema-border)] rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-[var(--tema-border)]">
              <h2 className="font-heading font-semibold text-[var(--tema-text)]">Cancelar agendamento</h2>
              <button onClick={() => setCancelTarget(null)} className="text-[var(--tema-text-muted)] hover:text-[var(--tema-text)]"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-1.5">
              <p className="text-sm text-[var(--tema-text-soft)]">
                Tem certeza que deseja cancelar o agendamento de{' '}
                <strong className="text-[var(--tema-text)]">{cancelTarget.client_nome}</strong>, às{' '}
                <strong className="text-[var(--tema-text)]">{cancelTarget.start_time}</strong>?
              </p>
              <p className="text-xs text-[var(--tema-text-muted)]">Essa ação não pode ser desfeita. O cliente será avisado por WhatsApp. O agendamento vai para a aba "Cancelados".</p>
            </div>
            <div className="p-5 border-t border-[var(--tema-border)] flex justify-end gap-3">
              <button onClick={() => setCancelTarget(null)} className="px-4 py-2 text-sm text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-xl transition-colors">Voltar</button>
              <button onClick={confirmarCancelamento} className="bg-red-500 hover:bg-red-400 text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">
                Cancelar agendamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão permanente de agendamento */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[var(--tema-bg-card)] border border-[var(--tema-border)] rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-[var(--tema-border)]">
              <h2 className="font-heading font-semibold text-[var(--tema-text)]">Excluir agendamento</h2>
              <button onClick={() => setDeleteTarget(null)} className="text-[var(--tema-text-muted)] hover:text-[var(--tema-text)]"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-1.5">
              <p className="text-sm text-[var(--tema-text-soft)]">
                Tem certeza que deseja excluir permanentemente o agendamento de{' '}
                <strong className="text-[var(--tema-text)]">{deleteTarget.client_nome}</strong>, às{' '}
                <strong className="text-[var(--tema-text)]">{deleteTarget.start_time}</strong>?
              </p>
              <p className="text-xs text-[var(--tema-text-muted)]">Essa ação não pode ser desfeita e o agendamento some da agenda. O cliente não é notificado.</p>
            </div>
            <div className="p-5 border-t border-[var(--tema-border)] flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-xl transition-colors">Voltar</button>
              <button onClick={confirmarExclusao} className="bg-red-500 hover:bg-red-400 text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">
                Excluir agendamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de configuração da agenda */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg my-8">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <h2 className="font-heading font-semibold text-white">Configurar horários da agenda</h2>
              <button onClick={() => setShowSettings(false)} className="text-zinc-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Duração do horário (min)</label>
                  <input type="number" min={5} step={5} className="field-input"
                    value={settings.duracao_slot_min}
                    onChange={e => setSettings(p => ({ ...p, duracao_slot_min: Number(e.target.value) }))} />
                </div>
                <div>
                  <label className="field-label">Capacidade simultânea</label>
                  <input type="number" min={1} className="field-input"
                    value={settings.capacidade}
                    onChange={e => setSettings(p => ({ ...p, capacidade: Number(e.target.value) }))} />
                </div>
                <div>
                  <label className="field-label">Antecedência mínima para agendar (horas)</label>
                  <input type="number" min={0} className="field-input"
                    value={settings.antecedencia_minima_horas}
                    onChange={e => setSettings(p => ({ ...p, antecedencia_minima_horas: Number(e.target.value) }))} />
                </div>
                <div>
                  <label className="field-label">Avisar cliente por WhatsApp (min. antes)</label>
                  <input type="number" min={1} className="field-input"
                    value={settings.lembrete_antecedencia_min}
                    onChange={e => setSettings(p => ({ ...p, lembrete_antecedencia_min: Number(e.target.value) }))} />
                </div>
              </div>

              <div>
                <label className="field-label mb-2 block">Dias e horário de funcionamento</label>
                <div className="space-y-2">
                  {DIAS.map(([key, label]) => {
                    const dia = settings.horarios[key] || { aberto: false, inicio: '08:00', fim: '18:00' };
                    return (
                      <div key={key} className="flex items-center gap-2 bg-zinc-800/50 border border-zinc-800 rounded-lg px-3 py-2">
                        <label className="flex items-center gap-2 w-28 shrink-0 cursor-pointer">
                          <input type="checkbox" checked={dia.aberto} onChange={e => setDia(key, 'aberto', e.target.checked)} />
                          <span className="text-sm text-white">{label}</span>
                        </label>
                        <input type="time" className="field-input py-1.5" disabled={!dia.aberto}
                          value={dia.inicio || '08:00'} onChange={e => setDia(key, 'inicio', e.target.value)} />
                        <span className="text-zinc-500 text-xs">até</span>
                        <input type="time" className="field-input py-1.5" disabled={!dia.aberto}
                          value={dia.fim || '18:00'} onChange={e => setDia(key, 'fim', e.target.value)} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="field-label">Imagem de fundo (aparece atrás da tela de agendamento do cliente)</label>
                <div className="flex items-center gap-3">
                  {settings.fundo_imagem && <img src={settings.fundo_imagem} alt="Fundo" className="h-12 w-12 object-cover rounded-lg border border-zinc-700 bg-zinc-800" />}
                  <label className="flex items-center gap-2 border border-zinc-700 hover:border-orange-500/50 text-zinc-300 hover:text-orange-400 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm">
                    <Upload size={14} />
                    {settings.fundo_imagem ? 'Trocar imagem' : 'Carregar imagem'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleFundoUpload} />
                  </label>
                  {settings.fundo_imagem && (
                    <button onClick={() => setSettings(p => ({ ...p, fundo_imagem: null }))}
                      className="text-zinc-500 hover:text-red-400 transition-colors"><X size={16} /></button>
                  )}
                </div>
              </div>

              <div>
                <label className="field-label">Mensagem de boas-vindas (aparece na tela de agendamento do cliente)</label>
                <textarea className="field-input min-h-[70px]" placeholder="Ex: Seja bem-vindo(a)! Agende seu horário e chegue com 10 min de antecedência."
                  value={settings.mensagem_boas_vindas}
                  onChange={e => setSettings(p => ({ ...p, mensagem_boas_vindas: e.target.value }))} />
              </div>

              <div>
                <label className="field-label mb-2 block">Datas bloqueadas (feriados, folgas)</label>
                <div className="flex items-center gap-2 mb-2">
                  <input type="date" className="field-input" value={novaDataBloqueada} onChange={e => setNovaDataBloqueada(e.target.value)} />
                  <button onClick={adicionarDataBloqueada} className="p-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl transition-colors">
                    <Plus size={15} />
                  </button>
                </div>
                {settings.datas_bloqueadas.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {settings.datas_bloqueadas.map(d => (
                      <span key={d} className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs px-2.5 py-1 rounded-full">
                        {d}
                        <button onClick={() => removerDataBloqueada(d)} className="text-zinc-500 hover:text-red-400"><Trash2 size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-3">
              <button onClick={() => setShowSettings(false)} className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors">Cancelar</button>
              <button onClick={salvarConfiguracoes} disabled={savingSettings}
                className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">
                {savingSettings ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal do link único de agendamento da empresa */}
      <BookingLinkModal
        open={!!companyLink}
        mandatory={false}
        link={companyLink}
        slug={slug}
        slugInput={slugInput}
        setSlugInput={setSlugInput}
        savingSlug={savingSlug}
        onSave={salvarSlug}
        onCopy={copiarLinkEmpresa}
        onClose={() => setCompanyLink(null)}
      />

      {/* Modal de fechamento de pagamento (Ir para pagamento) */}
      {vendaTarget && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-[var(--tema-bg-card)] border border-[var(--tema-border)] rounded-2xl w-full max-w-lg my-8">
            <div className="flex items-center justify-between p-5 border-b border-[var(--tema-border)]">
              <h2 className="font-heading font-semibold text-[var(--tema-text)]">Ir para pagamento</h2>
              <button onClick={() => setVendaTarget(null)} className="text-[var(--tema-text-muted)] hover:text-[var(--tema-text)]"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm">
                <div className="font-semibold text-[var(--tema-text)]">{vendaTarget.client_nome}</div>
                <div className="text-xs text-[var(--tema-text-muted)]">
                  {format(new Date(`${vendaTarget.date}T00:00:00`), 'dd/MM/yyyy')} às {vendaTarget.start_time}
                </div>
              </div>

              <div className="space-y-2">
                <label className="field-label">Itens do atendimento</label>
                {vendaItens.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input className="field-input flex-1" placeholder="Descrição do serviço"
                      value={it.descricao} onChange={e => setVendaItemDescricao(idx, e.target.value)} />
                    <input type="text" inputMode="numeric" className="field-input w-32 text-right"
                      value={brl(it.valor)} onChange={e => setVendaItemValor(idx, e.target.value)} />
                    <button onClick={() => removerVendaItem(idx)} className="text-red-400 hover:text-red-300 shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <select className="field-select" value=""
                  onChange={e => { if (e.target.value) adicionarVendaItemDoServico(e.target.value); }}>
                  <option value="">+ Adicionar item do catálogo</option>
                  {catalogoServicos.map(s => (
                    <option key={s.id} value={s.id}>{s.descricao} — {brl(s.valor)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="field-label">Forma de pagamento</label>
                <select className="field-select" value={vendaFormaPagamento} onChange={e => setVendaFormaPagamento(e.target.value)}>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="pix">Pix</option>
                  <option value="cartao">Cartão</option>
                </select>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[var(--tema-border)]">
                <span className="text-sm text-[var(--tema-text-muted)]">Total</span>
                <span className="text-xl font-bold text-[var(--tema-highlight)]">
                  {brl(vendaItens.reduce((acc, it) => acc + (Number(it.valor) || 0), 0))}
                </span>
              </div>
            </div>
            <div className="p-5 border-t border-[var(--tema-border)] flex justify-end gap-3">
              <button onClick={() => setVendaTarget(null)} className="px-4 py-2 text-sm text-[var(--tema-text-muted)] hover:text-[var(--tema-text)] border border-[var(--tema-border)] rounded-xl transition-colors">Voltar</button>
              <button onClick={confirmarPagamento} disabled={savingVenda}
                className="bg-[var(--tema-accent)] hover:opacity-90 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">
                {savingVenda ? 'Registrando...' : 'Confirmar pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
